import { useEffect, useState } from "preact/hooks";
import {
  MODIFIER_KEYS,
  required,
  styled,
  ToucanButton,
  ToucanEmptyNote,
  ToucanKeyCap,
} from "toucan";
import { ReviewCard } from "../components/ReviewCard.tsx";
import {
  dueCards,
  reviewPairs,
  reviewScopeIds,
  reviewSummaryCounts,
} from "../model.ts";
import { go, reviewPath } from "../routes.ts";
import { gradeSession, startSession, trickyLine } from "../session.ts";
import { store } from "../state.ts";
import type { Session, SessionKind } from "../session.ts";

/* Review page: the quiz's warm full-screen stage, laid out as a routed page.
   Positioned so the session bar can float at the top out of flow, leaving the
   card centred in the whole stage rather than pushed down below it. */
const Page = styled("section")`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 1.5rem 1.25rem;
  background: var(--toucan-canvas);

  .review-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
  }

  /* Out of flow at the top so it doesn't shift the card's centre; the stage's
     own top padding sets where it sits. */
  .review-progress {
    position: absolute;
    top: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    width: min(280px, 70%);
  }

  .review-progress-track {
    width: 100%;
    height: 0.375rem;
    overflow: hidden;
    border-radius: 999px;
    background: var(--toucan-track);
  }

  .review-progress-fill {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--toucan-success);
  }

  .review-progress-count {
    margin: 0;
    color: var(--toucan-muted);
    font-size: 0.9rem;
    font-weight: 600;
  }

  .review-complete {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    /* No wider than a dialog's card, so centred lines stay short enough to read
       on a desktop. */
    max-width: 510px;
    text-align: center;
    /* Centred lines of even length, rather than a long line and a stray word. */
    text-wrap: balance;
  }

  .review-heading {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
  }

  /* A count written the way the list header's score is, set a step up from
     the note under it. */
  .review-stat {
    margin: 0;
    color: var(--toucan-muted);
    font-size: 1.15rem;
    font-weight: 600;
  }
`;

/** How far through the session the progress bar is. */
function Progress({ answered, total }: { answered: number; total: number }) {
  const percent = Math.round((answered / total) * 100);
  return (
    <div class="review-progress">
      <span class="review-progress-track">
        <span
          class="review-progress-fill"
          style={{ width: `${String(percent)}%` }}
        />
      </span>
      <p class="review-progress-count">
        {answered} of {total}
      </p>
    </div>
  );
}

/** What a finished session says, and what its one button does. */
interface Finish {
  heading: string;
  /** A count in the list header's style, under the heading, or null for none. */
  stat: string | null;
  /** The paragraph under that, or "" for none. */
  note: string;
  button: string;
  act: () => void;
}

/**
 * The screen a finished session ends on. After Learn it says how much of
 * today is left, or that there is none. After Revise it gives the score.
 */
function finish(
  session: Session,
  scope: string[],
  tagId: string | undefined,
  again: () => void,
): Finish {
  const due = dueCards(store(), scope).length;
  const switchTo = (kind: SessionKind) => (): void => {
    go(reviewPath(kind, tagId));
  };
  if (session.kind === "revise") {
    return {
      heading: "Well done, you’ve finished revising!",
      stat: `${String(session.right)}/${String(session.answered)} right`,
      note: "",
      ...(due > 0
        ? { button: "Learn", act: switchTo("learn") }
        : { button: "Revise more", act: again }),
    };
  }
  const tricky = trickyLine(
    session.tricky.flatMap((id) => {
      const person = store().people.find((entry) => entry.id === id);
      return person === undefined ? [] : [person.name];
    }),
  );
  if (due > 0) {
    return {
      heading: "Well done, you’ve completed the session!",
      stat: `${String(due)} more today`,
      note: tricky,
      button: "Keep learning",
      act: again,
    };
  }
  const counts = reviewSummaryCounts(store(), scope);
  return {
    heading: "Well done, that’s everything for today!",
    // Nobody left in scope, synced away since, is nobody to be known.
    stat:
      counts.total > 0
        ? `${String(counts.known)}/${String(counts.total)} ${counts.total === 1 ? "person" : "people"} known`
        : null,
    note:
      tricky === ""
        ? "Come back tomorrow, or revise for extra practice."
        : `${tricky} Come back then, or revise for extra practice.`,
    button: "Revise",
    act: switchTo("revise"),
  };
}

/**
 * The review quiz, which asks a Learn or a Revise session's worth of
 * spaced-repetition flashcards one at a time. A desktop keyboard can drive it.
 */
export function ReviewPage({
  kind,
  tagId,
  activeTagIds,
  hints,
  onHints,
}: {
  kind: SessionKind;
  tagId: string | undefined;
  activeTagIds: string[];
  hints: boolean;
  onHints: () => void;
}) {
  const scope = reviewScopeIds(store(), activeTagIds);
  const cards = reviewPairs(store(), scope).length;
  const [session, setSession] = useState<Session | null>(() =>
    cards > 0 ? startSession(store(), scope, kind) : null,
  );
  const [revealed, setRevealed] = useState(false);

  // A synced doc landing someone straight here (deep link, reload) can be a
  // moment behind the page, and the session starts once their cards arrive.
  useEffect(() => {
    if (session === null && cards > 0) {
      setSession(startSession(store(), scope, kind));
    }
  }, [session, cards, kind, scope]);

  function again(): void {
    setRevealed(false);
    setSession(startSession(store(), scope, kind));
  }

  function grade(correct: boolean): void {
    const current = required(session, "session");
    setRevealed(false);
    setSession(
      gradeSession(
        store(),
        scope,
        current,
        required(current.card, "card"),
        correct,
      ),
    );
  }

  const ended =
    session !== null && session.card === null
      ? finish(session, scope, tagId, again)
      : null;

  // Desktop keyboard for the flashcards. Space reveals the answer, then 1 / ←
  // grades a miss and 2 / → grades a hit. Bound to the window, so it fires
  // however focus has drifted — onto a graded button that then vanished, say.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // A modifier, or a chord holding one (Alt+Tab, Cmd+R), is not review
      // input and neither shows the hints nor grades.
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        MODIFIER_KEYS.has(event.key)
      ) {
        return;
      }
      // Until the hints show, a key press only shows them, so the keys are
      // visible before one acts.
      if (!hints) {
        event.preventDefault();
        onHints();
        return;
      }
      // Space is the one key the finished screen answers to, and it means what
      // it meant on every card before it: go on.
      if (ended !== null) {
        if (event.key === " ") {
          event.preventDefault();
          ended.act();
        }
        return;
      }
      if (!revealed) {
        // Only Space flips the card — it matches the hint on the Reveal button.
        if (event.key === " ") {
          event.preventDefault();
          setRevealed(true);
        }
        return;
      }
      if (event.key === "1" || event.key === "ArrowLeft") {
        event.preventDefault();
        grade(false);
      } else if (event.key === "2" || event.key === "ArrowRight") {
        event.preventDefault();
        grade(true);
      }
    };
    // Re-bound on every render, with no dependency list: the handler closes
    // over the session and the reveal, and both change under it.
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });

  // The card on show and whoever it is about. The session picked it from the
  // store, so that person is in it.
  const shown = session === null ? null : session.card;
  const card =
    shown === null
      ? null
      : {
          round: shown,
          person: required(
            store().people.find((entry) => entry.id === shown.personId),
            "person",
          ),
        };
  return (
    <Page>
      {session !== null && session.total > 0 && (
        <Progress answered={session.answered} total={session.total} />
      )}
      <div class="review-body">
        {ended !== null ? (
          <div class="review-complete">
            <p class="review-heading">{ended.heading}</p>
            {ended.stat !== null && <p class="review-stat">{ended.stat}</p>}
            {ended.note !== "" && (
              <ToucanEmptyNote class="review-empty">
                {ended.note}
              </ToucanEmptyNote>
            )}
            <ToucanButton variant="primary" onClick={ended.act}>
              {ended.button}
              {hints && <ToucanKeyCap>Space</ToucanKeyCap>}
            </ToucanButton>
          </div>
        ) : card === null ? (
          <ToucanEmptyNote class="review-empty">
            Nothing to review yet — add a photo to someone, then come back.
          </ToucanEmptyNote>
        ) : (
          <ReviewCard
            direction={card.round.direction}
            name={card.person.name}
            photo={card.round.photo}
            revealed={revealed}
            hints={hints}
            onReveal={() => {
              setRevealed(true);
            }}
            onGrade={grade}
          />
        )}
      </div>
    </Page>
  );
}
